import { Bell, Mail, X } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

const TIPO_ICON = {
  cotizacion_aprobada:  '✅',
  cotizacion_rechazada: '❌',
  caso_asignado:        '📋',
  visita_reprogramada:  '🔄',
  qc_aprobado:          '✅',
  qc_rechazado:         '⚠️',
  ot_emitida:           '📋',
  default:              '🔔',
};

function formatTiempo(d) {
  if (!d) return '';
  const diff = (Date.now() - new Date(d).getTime()) / 60000;
  if (diff < 1)   return 'recién';
  if (diff < 60)  return `hace ${Math.floor(diff)} min`;
  if (diff < 1440) return `hace ${Math.floor(diff / 60)} h`;
  if (diff < 10080) return `hace ${Math.floor(diff / 1440)} d`;
  return new Date(d).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
}

export default function NotificacionesPanel({ userId, onClose }) {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('notificaciones')
      .select('id, tipo, titulo, cuerpo, link, caso_id, leida, created_at')
      .eq('destinatario_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    setItems(data || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    cargar();
    function onEsc(e) { if (e.key === 'Escape') onClose?.(); }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [cargar, onClose]);

  async function marcarTodasLeidas() {
    const noLeidas = items.filter(n => !n.leida).map(n => n.id);
    if (!noLeidas.length) return;
    await supabase.from('notificaciones').update({ leida: true, leida_at: new Date().toISOString() }).in('id', noLeidas);
    cargar();
  }

  async function marcarLeida(id) {
    await supabase.from('notificaciones').update({ leida: true, leida_at: new Date().toISOString() }).eq('id', id);
    setItems(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
  }

  const noLeidas = items.filter(n => !n.leida).length;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 12,
        width: '100%', maxWidth: 480, border: '0.5px solid var(--border-200)',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '0.5px solid var(--border-200)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-strong)' }}><Bell size={16} strokeWidth={1.75} aria-hidden style={{verticalAlign:"-3px",display:"inline-block"}} /> Notificaciones</h2>
            <div style={{ fontSize: 12, color: 'var(--ink-400)', marginTop: 2 }}>
              {noLeidas > 0 ? `${noLeidas} sin leer` : 'Todas leídas'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {noLeidas > 0 && (
              <button type="button" onClick={marcarTodasLeidas}
                style={{ padding: '6px 10px', border: '0.5px solid var(--border-200)', borderRadius: 6,
                  background: 'transparent', color: 'var(--ink-500)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                Marcar todas
              </button>
            )}
            <button type="button" onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--ink-400)' }}><X size={16} strokeWidth={1.75} aria-hidden style={{verticalAlign:"-3px",display:"inline-block"}} /></button>
          </div>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-400)', fontSize: 13 }}>Cargando…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--ink-400)' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}><Mail size={16} strokeWidth={1.75} aria-hidden style={{verticalAlign:"-3px",display:"inline-block"}} /></div>
              <div style={{ fontSize: 13 }}>No tienes notificaciones todavía</div>
            </div>
          ) : (
            items.map(n => (
              <div key={n.id}
                onClick={() => !n.leida && marcarLeida(n.id)}
                style={{
                  padding: '12px 20px', borderBottom: '0.5px solid var(--border-200)',
                  display: 'flex', gap: 12, cursor: n.leida ? 'default' : 'pointer',
                  background: n.leida ? 'transparent' : 'var(--bg-muted)',
                  transition: 'background 0.15s',
                }}>
                <div style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>
                  {TIPO_ICON[n.tipo] || TIPO_ICON.default}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                    <span style={{
                      fontSize: 13, fontWeight: n.leida ? 400 : 600,
                      color: 'var(--text-strong)',
                    }}>{n.titulo}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-400)', flexShrink: 0 }}>
                      {formatTiempo(n.created_at)}
                    </span>
                  </div>
                  {n.cuerpo && (
                    <div style={{ fontSize: 12, color: 'var(--ink-500)', lineHeight: 1.4 }}>{n.cuerpo}</div>
                  )}
                </div>
                {!n.leida && (
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: 'var(--accent, #FF6428)', flexShrink: 0, marginTop: 8,
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
